import { useEffect, useMemo, useState } from "react";
import t from "@/lang/lang";
import {
  useQpmOfferAddCompanyReferencesToOfferMutation,
  useQpmOfferAddCompanyReferenceToOfferMutation,
  useQpmOfferDeleteOfferCompanyReferenceMutation,
  useQpmOfferShowQuery,
  useQpmOfferUpdateOfferCompanyReferenceMutation,
} from "@/services/backend/qpm/offers/api/offer-api";
import { useQpmCompanyReferenceListQuery } from "@/services/backend/qpm/company-references/api/company-reference-api";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { ChevronsUpDown, Trash2 } from "lucide-react";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";

type BaseReference = {
  id: string;
  branch: string;
  companyName: string;
};

type SnapshotReference = {
  sourceId: string;
  branch: string;
  companyName: string;
  display: boolean;
};

export function CompanyReferences({ offerId }: { offerId: string }) {
  const { toast } = useToast();
  const { companyId } = useActiveCompany();

  const [branch, setBranch] = useState<string>("");
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState<string>("");

  const {
    data: offer,
    isLoading: isLoadingOffer,
    error: errorOffer,
  } = useQpmOfferShowQuery({ id: offerId }, { skip: !offerId });

  const {
    data: baseList,
    isLoading: isLoadingBase,
    error: errorBase,
  } = useQpmCompanyReferenceListQuery({
    company: { active: true, values: [companyId] },
  });

  const [addOne, addOneState] = useQpmOfferAddCompanyReferenceToOfferMutation();
  const [addMany, addManyState] =
    useQpmOfferAddCompanyReferencesToOfferMutation();
  const [update, updateState] =
    useQpmOfferUpdateOfferCompanyReferenceMutation();
  const [remove, removeState] =
    useQpmOfferDeleteOfferCompanyReferenceMutation();

  useEffect(() => {
    const err =
      addOneState.error ||
      addManyState.error ||
      updateState.error ||
      removeState.error;

    if (!err) return;

    toast({ ...parseRTKQueryError(err) });
    addOneState.reset();
    addManyState.reset();
    updateState.reset();
    removeState.reset();
  }, [addManyState, addOneState, removeState, toast, updateState]);

  const baseItems: BaseReference[] = useMemo(() => {
    const items = baseList?.items ?? [];
    return items.map((it) => ({
      id: it.id,
      branch: it.branch ?? "",
      companyName: it.companyName ?? "",
    }));
  }, [baseList?.items]);

  const baseItemsSorted = useMemo(
    () =>
      [...baseItems].sort(
        (a, b) =>
          (a.branch || "").localeCompare(b.branch || "") ||
          (a.companyName || "").localeCompare(b.companyName || ""),
      ),
    [baseItems],
  );

  const branches = useMemo(() => {
    const set = new Set(baseItemsSorted.map((x) => x.branch).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [baseItemsSorted]);

  const snapshotItems: SnapshotReference[] = useMemo(() => {
    const refs = offer?.companyReferenceSnapshot?.references ?? [];
    return refs
      .map((r) => ({
        sourceId: r.snapshot.sourceId,
        branch: r.snapshot.branch,
        companyName: r.snapshot.companyName,
        display: r.display,
      }))
      .sort(
        (a, b) =>
          (a.branch || "").localeCompare(b.branch || "") ||
          (a.companyName || "").localeCompare(b.companyName || ""),
      );
  }, [offer?.companyReferenceSnapshot?.references]);

  const selectedIds = useMemo(
    () => new Set(snapshotItems.map((x) => x.sourceId)),
    [snapshotItems],
  );

  const availableForSingleAdd = useMemo(
    () => baseItemsSorted.filter((it) => !selectedIds.has(it.id)),
    [baseItemsSorted, selectedIds],
  );

  const handleAddBranch = async () => {
    if (!offer || offer.immutable) return;
    if (!branch) return;
    const ids = baseItemsSorted
      .filter((x) => x.branch === branch)
      .map((x) => x.id)
      .filter((id) => !selectedIds.has(id));

    if (ids.length === 0) return;

    await addMany({ offerId, companyReferenceIds: ids }).unwrap();
  };

  const handleAddOne = async (companyReferenceId: string) => {
    if (!offer || offer.immutable) return;
    await addOne({ offerId, companyReferenceId }).unwrap();
    setSearchValue("");
    setOpen(false);
  };

  const handleDelete = async (companyReferenceId: string) => {
    if (!offer || offer.immutable) return;
    await remove({ offerId, companyReferenceId }).unwrap();
  };

  const handleToggleDisplay = async (
    item: SnapshotReference,
    display: boolean,
  ) => {
    if (!offer || offer.immutable) return;
    await update({
      offerId,
      companyReferenceId: item.sourceId,
      display,
      branch: item.branch,
      companyName: item.companyName,
    }).unwrap();
  };

  if (isLoadingOffer || isLoadingBase) {
    return (
      <div className="text-muted-foreground animate-pulse">
        {t("Lade Daten...")}
      </div>
    );
  }

  if (errorOffer) {
    return <RTKQueryErrorAlert error={errorOffer} />;
  }

  if (errorBase) {
    return <RTKQueryErrorAlert error={errorBase} />;
  }

  if (!offer) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="text-base font-semibold">{t("Firmenreferenzen")}</div>

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex min-w-65 flex-col gap-1">
            <div className="text-muted-foreground text-sm">{t("Filiale")}</div>
            <Select
              value={branch}
              onValueChange={setBranch}
              disabled={offer.immutable}
            >
              <SelectTrigger className="min-w-65">
                <SelectValue placeholder={t("Filiale auswählen")} />
              </SelectTrigger>
              <SelectContent>
                {branches.map((b) => (
                  <SelectItem key={b} value={b}>
                    {b}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            type="button"
            onClick={() => {
              handleAddBranch().catch(() => undefined);
            }}
            disabled={!branch || offer.immutable}
          >
            {t("Alle hinzufügen")}
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className="min-w-65 justify-between"
                disabled={offer.immutable}
              >
                {t("Referenz suchen und hinzufügen")}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="min-w-65 p-0"
              side="bottom"
              align="start"
            >
              <Command>
                <CommandInput
                  placeholder={t("Suchen...")}
                  value={searchValue}
                  onValueChange={setSearchValue}
                  disabled={offer.immutable}
                />
                <CommandList>
                  <CommandEmpty>{t("Keine Referenz gefunden.")}</CommandEmpty>
                  <CommandGroup heading={t("Verfügbare Referenzen")}>
                    {availableForSingleAdd.map((ref) => (
                      <CommandItem
                        key={ref.id}
                        onSelect={() => {
                          handleAddOne(ref.id).catch(() => undefined);
                        }}
                      >
                        <div className="flex w-full flex-col">
                          <div className="font-medium">{ref.companyName}</div>
                          <div className="text-muted-foreground text-xs">
                            {ref.branch}
                          </div>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {snapshotItems.map((ref) => (
          <div
            key={ref.sourceId}
            className="flex items-center justify-between gap-2 rounded-md border px-3 py-2"
          >
            <div className="flex flex-col">
              <div className="font-medium">{ref.companyName}</div>
              <div className="text-muted-foreground text-xs">{ref.branch}</div>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={ref.display}
                  disabled={offer.immutable}
                  onCheckedChange={(checked) => {
                    handleToggleDisplay(ref, checked === true).catch(
                      () => undefined,
                    );
                  }}
                />
                <div className="text-muted-foreground text-sm">
                  {t("Anzeigen")}
                </div>
              </div>

              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                title={t("Löschen")}
                disabled={offer.immutable}
                onClick={() => {
                  handleDelete(ref.sourceId).catch(() => undefined);
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}

        {snapshotItems.length === 0 && (
          <div className="text-muted-foreground text-sm">
            {t("Keine Firmenreferenzen ausgewählt.")}
          </div>
        )}
      </div>
    </div>
  );
}
